// @flow

import * as React from 'react';
import { NavTabs } from '@ta/styleguide.nav-tabs';
import type { HoverChainNode } from '@ta/common.hover-chain';
import CollapsedMenu from './CollapsedMenu';
import CollapsibleListItem from './CollapsibleListItem';
import Menu from './Menu';
import MenuTab from './MenuTab';
import MenuList from './MenuList';
import MenuListItem from './MenuListItem';
import type { NavLinkType } from '../types';
import type { Position } from './Menu';
import styles from './NavLinks.less';

/** Identifier for the collapsed contents */
type CollapsedItemName = string;

/** Contents to show in the collapsed menu when a menu item is considered collapsed (out of view). Root node should be <li>. */
type CollapsedItemContents = React.Node;

/** A single collapsed item, describing the contents to show in the collapsed menu as well as the order to show it in in respect to other collapsed items */
export type CollapsedItem = {|
  order: number,
  name: CollapsedItemName,
  contents: CollapsedItemContents,
|};

type Props = {|
  /** Links to show */
  links: NavLinkType[],

  /** ID of the link to show as active */
  activeLinkId: ?string,

  /**
   * Whether the nav links should only show in page source for crawlers. We does this because on mobile we don't mount the sidebar until
   * the user opens it, which _would_ show the same links (but via a different component, SideBar.jsx).
   */
  sourceOnly: boolean,
|};

type State = {|
  collapsedItems: CollapsedItem[],
|};

/**
 * Presentational nav component that takes links and displays them, generates menus, and collapses overflowed links
 * into a "more" menu.
 * Tracking, menus, and other link behavior is defined by each link (see NavLink type).
 *
 * This is separate from quick links, which is a separate navigational component we display in certain situations.
 */
class NavLinks extends React.Component<Props, State> {
  static defaultProps = {
    sourceOnly: false,
  };

  state = {
    collapsedItems: [],
  };

  getForcedCollapsedLinks = (): CollapsedItem[] =>
    this.props.links
      .filter(link => link.forceCollapse)
      .map(link => ({
        order: 0,
        name: link.id,
        contents: <MenuListItem link={link} key={`${link.id}_${link.text}`} />,
      }));

  /**
   * Adds an item to the list of collapsed items because it's now invisible.
   * @param order Order the menu items should be displayed in the collapsed menu.
   * @param name Unique name so we can keep track for when uncollapsedHAndler is called.
   * @param contents Contents to show in the collapsed menu.
   */
  collapsedHandler = (order: number, name: CollapsedItemName, contents: CollapsedItemContents) => {
    // First let's make sure this item is not already in the collapsed list. E.g., a resize of the window caused a API reload and
    // we add a second link of the same name to the collapsed list.
    if (this.state.collapsedItems.filter(item => item.name === name).length > 0) {
      return;
    }

    // Use the callback version of setState because on load we could be calling collapsedHandler many times and we need to make
    // sure we preserve each of the items that collapsed on first mount.
    this.setState(prevState => {
      const sortedList = [{ order, name, contents }, ...prevState.collapsedItems].sort(
        (o1, o2) => o1.order - o2.order,
      );
      return { collapsedItems: sortedList };
    });
  };

  /**
   * Removes an item from the list of collapsed items because it's now visible
   * @param name Unique name that we track so we can figure out which list item to remove from the collapsed menu.
   */
  uncollapsedHandler = (name: CollapsedItemName) => {
    // Use the callback version of setState in case this function is called twice between state updates.
    this.setState(prevState => ({
      collapsedItems: prevState.collapsedItems.filter(item => item.name !== name),
    }));
  };

  /**
   * Helper function that will render a CollapsibleListItem.
   * The callbacks passed to CollapsibleListItem will wrap our collapsedHandler and unCollapsedHandler
   * with the collapsedContents passed into this function.
   */
  createCollapsibleListItem = (
    order: number,
    name: CollapsedItemName,
    collapsedContents: CollapsedItemContents,
    defaultContents: React.Node,
  ) => (
    <CollapsibleListItem
      collapsedCallback={() => this.collapsedHandler(order, name, collapsedContents)}
      uncollapsedCallback={() => this.uncollapsedHandler(name)}
      key={`${name}_${order}`}
    >
      {defaultContents}
    </CollapsibleListItem>
  );

  createMenuList = (
    links: NavLinkType[],
    parentPtr?: HoverChainNode,
    topLevelPosition?: Position,
    minHeight: number,
  ) =>
    links.map<React.Node>(link => {
      if (!link.subList || !link.subList.length) {
        return <MenuListItem link={link} key={`${link.id}_${link.text}`} />;
      }
      const anchorItem = <MenuListItem link={link} key={`${link.id}_${link.text}`} hasSubItem />;
      return (
        <Menu
          key={`${link.id}_${link.text}`}
          triggerActive={false}
          tabType="nonlink"
          nonLinkContents={anchorItem}
          hoverChainParent={parentPtr}
          topLevelPosition={topLevelPosition}
          isAside
        >
          {(ptr, position) => (
            <MenuList minHeight={`${minHeight}px`}>
              {this.createMenuList(link.subList, ptr, position, minHeight)}
            </MenuList>
          )}
        </Menu>
      );
    });

  createTopLevelMenuItem = (link: NavLinkType, isActive: boolean) => {
    if (!link.subList || !link.subList.length) {
      return <MenuTab link={link} key={`${link.id}_${link.text}`} active={isActive} />;
    }
    this.topLevelMenuList.set(link.id, React.createRef());
    return (
      <Menu triggerActive={isActive} tabType="link" link={link}>
        {(ptr, topLevelPosition) => {
          const childElement = this.createMenuList(
            link.subList,
            ptr,
            topLevelPosition,
            this.topLevelMenuList.get(link.id)?.clientHeight || 0,
          );
          return (
            <MenuList
              setRef={el => {
                this.topLevelMenuList.set(link.id, el);
              }}
            >
              {childElement}
            </MenuList>
          );
        }}
      </Menu>
    );
  };

  topLevelMenuList: Map<string, React.ElementRef<*>> = new Map();

  render() {
    const { links, activeLinkId } = this.props;

    // Links can be force collapsed, and then they can be auto-collapsed when they overflow. The forced collapsed links need to be
    // place directly in the CollapsedMenu.
    const forcedCollapseLinks = this.getForcedCollapsedLinks();

    const nonForcedCollapseLinks = links
      .filter(link => !link.forceCollapse)
      .map((link, index) =>
        this.createCollapsibleListItem(
          index,
          link.id,
          <MenuListItem link={link} key={`${link.id}_${link.text}`} />,
          this.createTopLevelMenuItem(link, !!(activeLinkId && activeLinkId === link.id)),
        ),
      );

    // Combine the dynamically collapsed items (that don't fit on a single line) with the items that were marked as forced-collapsed
    // in the link models, and stick them in the CollapsedMenu.
    // Edge case fix: When a link's forceCollapse is suddenly true for a particular breakpoint, we can get React keys warnings because
    // a dynamically-collapsed link has not unmounted (but is about to) and we're calling render with it in state.collapsedItems and
    // suddenly a link is forced into the CollapsedMenu and now we have duplicates links of the same key name for a very brief moment.
    const forcedCollapsedNames = forcedCollapseLinks.map(item => item.name);
    const allCollapsedItems = this.state.collapsedItems
      .filter(item => forcedCollapsedNames.indexOf(item.name) === -1)
      .concat(forcedCollapseLinks);

    return (
      <div className={this.props.sourceOnly ? styles.hidden : null}>
        <NavTabs className={styles.navMenuContainer}>
          <ul className={styles.navMenuList}>{nonForcedCollapseLinks}</ul>
          <CollapsedMenu items={allCollapsedItems} />
        </NavTabs>
      </div>
    );
  }
}

export default NavLinks;
